/**
 * # InputStepper
 *
 * A component that allows users to increment or decrement a numeric value using `+` or `-` buttons.
 *
 * ## Props
 *
 * - `id` (string): The ID of the input element.
 * - `ariaLabel` (string): The ARIA label for the input element.
 * - `className` (string): Additional CSS class name for the component.
 * - `error` (bool): Indicates whether there is an error with the input.
 * - `errorMessage` (string): The error message to display when there is an error.
 * - `max` (number): The maximum value allowed for the input.
 * - `min` (number): The minimum value allowed for the input.
 * - `onChange` (function): The callback function to be called when the value changes.
 * - `size` ('default' | 'small'): The size variant of the input stepper.
 * - `step` (number): The increment or decrement step value.
 * - `value` (number): The current value of the input.
 *
 * ## Usage
 *
 * ```jsx
 * import InputStepper from './InputStepper.react';
 *
 * const MyComponent = () => {
 *   const handleChange = (value) => {
 *     console.log('New value:', value);
 *   };
 *
 *   return (
 *     <InputStepper
 *       id="myInputStepper"
 *       ariaLabel="Input Stepper"
 *       className="my-input-stepper"
 *       error={false}
 *       errorMessage="Something went wrong"
 *       max={10}
 *       min={0}
 *       onChange={handleChange}
 *       size="default"
 *       step={2}
 *       value={4}
 *     />
 *   );
 * };
 *
 * export default MyComponent;
 * ```
 */
import React from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';
import {useTracking} from 'react-tracking';

import Input from '../../atoms/NumberInput/NumberInput.react';
import IconButton from '../IconButton/IconButton.react';
import Text from '../../atoms/Text/Text.react';

import './input-stepper.scss';

interface Props {
  id?: string;
  ariaLabel?: string;
  className?: string;
  error?: boolean;
  errorMessage?: string;
  max?: number;
  min?: number;
  onChange: (value: number | string) => void;
  size?: 'default' | 'small';
  step?: number;
  value: number;
  [key: string]: unknown;
}

const InputStepper = ({
  min = 0,
  max,
  step = 1,
  onChange,
  value,
  error,
  errorMessage = 'Something went wrong.',
  ariaLabel,
  id = 'inputStepper',
  className,
  size = 'default',
  ...rest
}: Props) => {
  const {trackEvent} = useTracking({type: 'button', event: 'click'});
  const iconButtonSize = size === 'small' ? 'xs' : 's';

  return (
    <div
      className={classnames('m-input-stepper', className, {
        'm-input-stepper--error': error,
        'm-input-stepper--small': size === 'small'
      })}
    >
      <div className='m-input-stepper__control'>
        <IconButton
          variant='faded'
          color='primary'
          size={iconButtonSize}
          roundness='square'
          label={`decrement by ${step}`}
          disabled={value <= min}
          icon='minus'
          onClick={() => {
            trackEvent({content: '-'});
            onChange(value - step);
          }}
        />
        <Input
          error={error}
          className='m-input-stepper__input'
          // @ts-expect-error `border` is an undeclared passthrough prop on the Input atom; retained to preserve runtime semantics
          border={false}
          size='s'
          max={max}
          min={min}
          value={value}
          step={step}
          onChange={(e) => onChange(e.target.value)}
          aria-label={ariaLabel}
          aria-describedby={error ? id : undefined}
          type='number'
          pattern='[0-9]*'
          {...rest}
        />
        <IconButton
          variant='faded'
          color='primary'
          size={iconButtonSize}
          roundness='square'
          label={`increment by ${step}`}
          disabled={max != null && value >= max}
          icon='plus'
          onClick={() => {
            trackEvent({content: '+'});
            onChange(value + step);
          }}
        />
      </div>
      {error && (
        <Text as='p' id={id} size='s' color='negative' className='m-input-stepper__text'>
          {errorMessage}
        </Text>
      )}
    </div>
  );
};

InputStepper.propTypes = {
  id: PropTypes.string,
  ariaLabel: PropTypes.string,
  className: PropTypes.string,
  error: PropTypes.bool,
  errorMessage: PropTypes.string,
  max: PropTypes.number,
  min: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  size: PropTypes.oneOf(['default', 'small']),
  step: PropTypes.number,
  value: PropTypes.number.isRequired
};

export default InputStepper;
